import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, UserPlus, Mail, RefreshCw, Trash2, X, Copy, ShieldCheck,
    UserCog, Users as UsersIcon, Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";

const STATUS_PILL = {
    pending: "bg-sky-100 text-sky-800 border-sky-200",
    accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
    revoked: "bg-rose-100 text-rose-800 border-rose-200",
    expired: "bg-rose-50 text-rose-700 border-rose-300 border-dashed",
};

const ROLE_PILL = {
    master_admin: "bg-mm-navy text-white",
    admin: "bg-cobalt-100 text-cobalt-800 border-cobalt-200",
    agent: "bg-zinc-100 text-zinc-700 border-zinc-200",
    lead: "bg-mm-sky/15 text-mm-blue border-mm-sky/30",
};

// Phase 12 — the role filter chip set.
const ROLE_FILTERS = [
    { value: "all", label: "All" },
    { value: "master_admin", label: "Master Admin" },
    { value: "admin", label: "Admin" },
    { value: "agent", label: "Agent" },
    { value: "lead", label: "Lead" },
];

export default function AdminUsers() {
    const { user } = useAuth();
    const [tab, setTab] = useState("users");

    return (
        <div className="space-y-5" data-testid="admin-users">
            <header>
                <div className="mm-eyebrow flex items-center gap-1.5"><UserCog size={11} /> Team</div>
                <h1 className="mm-display text-3xl text-mm-navy">Users & Invites</h1>
            </header>
            <nav className="flex items-center gap-1 border-b border-mm-line">
                <TabButton active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">
                    <UsersIcon size={13} /> Users
                </TabButton>
                <TabButton active={tab === "invites"} onClick={() => setTab("invites")} testid="tab-invites">
                    <Mail size={13} /> Invites
                </TabButton>
            </nav>
            {tab === "users" ? <UsersTab me={user} /> : <InvitesTab me={user} />}
        </div>
    );
}

function TabButton({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                active ? "border-mm-navy text-mm-navy" : "border-transparent text-mm-muted hover:text-mm-ink"
            }`}
            data-testid={testid}
        >
            {children}
        </button>
    );
}

/* ---------------- Users tab ---------------- */

function UsersTab({ me }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [managerEditFor, setManagerEditFor] = useState(null); // user obj currently editing
    // Phase 12 — role filter (default All).
    const [roleFilter, setRoleFilter] = useState("all");

    const load = useCallback(() => {
        setLoading(true);
        adminApi.listUsers()
            .then(setRows)
            .catch(() => toast.error("Couldn't load users."))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const canEditRole = me?.role === "master_admin";
    const canEditManager = me?.role === "master_admin" || me?.role === "admin";

    const changeRole = async (uid, role) => {
        try {
            await adminApi.changeUserRole(uid, role);
            toast.success("Role updated.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update role.");
        }
    };

    const visibleRows = useMemo(() => {
        if (roleFilter === "all") return rows;
        return rows.filter((u) => u.role === roleFilter);
    }, [rows, roleFilter]);

    return (
        <div className="mm-card overflow-hidden" data-testid="users-table">
            <div className="flex flex-wrap items-center gap-2 border-b border-mm-line bg-mm-bg/50 px-3 py-2.5" data-testid="users-role-filter">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-mm-muted">Role</span>
                {ROLE_FILTERS.map((rf) => (
                    <button
                        key={rf.value}
                        type="button"
                        onClick={() => setRoleFilter(rf.value)}
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                            roleFilter === rf.value
                                ? "border-mm-navy bg-mm-navy text-white"
                                : "border-mm-line bg-white text-mm-ink hover:bg-mm-bg"
                        }`}
                        data-testid={`users-role-filter-${rf.value}`}
                    >
                        {rf.label}
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                        <tr>
                            <th className="px-3 py-2.5 text-left font-medium">Name</th>
                            <th className="px-3 py-2.5 text-left font-medium">Email</th>
                            <th className="px-3 py-2.5 text-left font-medium">Role</th>
                            <th className="px-3 py-2.5 text-left font-medium">Provider</th>
                            <th className="px-3 py-2.5 text-left font-medium">Manager</th>
                            <th className="px-3 py-2.5 text-left font-medium">Last login</th>
                            <th className="px-3 py-2.5 text-left font-medium">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((u) => (
                            <tr key={u.id} className="border-t border-mm-line" data-testid={`user-row-${u.id}`}>
                                <td className="px-3 py-2.5 font-medium text-mm-navy">
                                    {u.first_name} {u.last_name}
                                    {!u.active && <span className="ml-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[9px] text-rose-700">inactive</span>}
                                </td>
                                <td className="px-3 py-2.5 text-mm-ink">{u.email}</td>
                                <td className="px-3 py-2.5">
                                    {canEditRole && u.role !== "lead" ? (
                                        <select
                                            value={u.role}
                                            onChange={(e) => changeRole(u.id, e.target.value)}
                                            className="rounded-md border border-mm-line bg-white px-2 py-0.5 text-xs"
                                            data-testid={`user-role-select-${u.id}`}
                                        >
                                            <option value="master_admin">Master Admin</option>
                                            <option value="admin">Admin</option>
                                            <option value="agent">Agent</option>
                                        </select>
                                    ) : (
                                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${ROLE_PILL[u.role] || ""}`}>
                                            {(u.role || "").replace("_", " ")}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5">
                                    {u.signup_source === "google_signup" ? (
                                        <span className="inline-flex items-center gap-1 rounded-full border border-mm-line bg-white px-2 py-0.5 text-[11px] font-medium text-mm-ink" data-testid={`user-provider-${u.id}`}>
                                            Google
                                        </span>
                                    ) : (
                                        <span className="text-[11px] text-mm-muted">Password</span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5">
                                    <ManagerCell
                                        user={u}
                                        allUsers={rows}
                                        canEdit={canEditManager && u.role !== "master_admin" && u.role !== "lead"}
                                        onEdit={() => setManagerEditFor(u)}
                                    />
                                </td>
                                <td className="px-3 py-2.5 text-[11px] text-mm-muted">
                                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}
                                </td>
                                <td className="px-3 py-2.5 text-[11px] text-mm-muted">
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                </td>
                            </tr>
                        ))}
                        {!visibleRows.length && (
                            <tr><td colSpan={7} className="px-3 py-10 text-center text-xs text-mm-muted" data-testid="users-empty">
                                No users match this filter.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            )}
            {managerEditFor && (
                <ManagerPickerModal
                    user={managerEditFor}
                    allUsers={rows}
                    onClose={() => setManagerEditFor(null)}
                    onSaved={() => { setManagerEditFor(null); load(); }}
                />
            )}
        </div>
    );
}

function ManagerCell({ user, canEdit, onEdit }) {
    if (user.role === "master_admin") {
        return <span className="text-[11px] text-mm-muted italic" data-testid={`user-manager-top-${user.id}`}>Top of chain</span>;
    }
    return (
        <div className="flex items-center gap-2">
            {user.manager_name ? (
                <div>
                    <div className="text-[13px] font-medium text-mm-ink" data-testid={`user-manager-name-${user.id}`}>{user.manager_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">{(user.manager_role || "").replace("_", " ")}</div>
                </div>
            ) : (
                <span className="text-[11px] text-mm-muted italic" data-testid={`user-manager-unassigned-${user.id}`}>Unassigned</span>
            )}
            {canEdit && (
                <button
                    onClick={onEdit}
                    className="ml-auto rounded-md border border-mm-line bg-white px-2 py-0.5 text-[10px] font-medium text-mm-navy hover:bg-mm-bg"
                    data-testid={`user-manager-edit-${user.id}`}
                >
                    Change
                </button>
            )}
        </div>
    );
}

function ManagerPickerModal({ user, allUsers, onClose, onSaved }) {
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);

    const candidates = useMemo(() => {
        const q = query.trim().toLowerCase();
        return allUsers
            .filter((u) => u.id !== user.id && (u.role === "admin" || u.role === "master_admin" || u.role === "agent"))
            .filter((u) => {
                if (!q) return true;
                const hay = `${u.first_name} ${u.last_name} ${u.email} ${u.role}`.toLowerCase();
                return hay.includes(q);
            })
            .slice(0, 40);
    }, [allUsers, query, user.id]);

    const setManager = async (mid) => {
        setSaving(true);
        try {
            await adminApi.changeUserManager(user.id, mid);
            toast.success(mid ? "Manager assigned." : "Manager cleared.");
            onSaved();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) toast.error("That assignment would create a cycle.");
            else if (status === 400) toast.error(err?.response?.data?.detail || "Invalid assignment.");
            else toast.error(err?.response?.data?.detail || "Couldn't assign manager.");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" role="dialog" data-testid="manager-picker-modal">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <div className="mm-eyebrow">Assign upline</div>
                <h3 className="mm-display mt-1 text-xl text-mm-navy">{user.first_name} {user.last_name}</h3>
                <p className="mt-1 text-[11px] text-mm-muted">
                    Current: <strong>{user.manager_name || "Unassigned"}</strong>. Changing this will re-parent {user.first_name} in the org tree.
                </p>
                <Input
                    placeholder="Search users (name, email, role)…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="mt-3"
                    data-testid="manager-picker-search"
                    autoFocus
                />
                <div className="mt-2 max-h-72 divide-y divide-mm-line overflow-y-auto rounded-lg border border-mm-line" data-testid="manager-picker-list">
                    <button
                        type="button"
                        onClick={() => setManager(null)}
                        disabled={saving || !user.manager_id}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-mm-ink hover:bg-mm-bg disabled:opacity-40"
                        data-testid="manager-picker-clear"
                    >
                        <X size={13} className="text-rose-600" />
                        <span>Clear manager (top of branch)</span>
                    </button>
                    {candidates.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => setManager(c.id)}
                            disabled={saving}
                            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-mm-bg disabled:opacity-40"
                            data-testid={`manager-picker-opt-${c.id}`}
                        >
                            <div>
                                <div className="font-medium text-mm-navy">{c.first_name} {c.last_name}</div>
                                <div className="text-[10px] uppercase tracking-wider text-mm-muted">{(c.role || "").replace("_", " ")} · {c.email}</div>
                            </div>
                            {c.id === user.manager_id && <Check size={14} className="text-emerald-600" />}
                        </button>
                    ))}
                    {candidates.length === 0 && (
                        <div className="px-3 py-4 text-center text-[11px] text-mm-muted">No matching users.</div>
                    )}
                </div>
                <div className="mt-4 flex items-center justify-end">
                    <button onClick={onClose} className="mm-btn-outline" data-testid="manager-picker-close">Close</button>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Invites tab ---------------- */

function InvitesTab({ me }) {
    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [draft, setDraft] = useState({ email: "", role: "agent", notes: "", manager_id: null });
    const [saving, setSaving] = useState(false);
    const [lastLink, setLastLink] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([adminApi.listInvites(), adminApi.listUsers().catch(() => [])])
            .then(([invites, u]) => { setRows(invites); setUsers(u || []); })
            .catch(() => toast.error("Couldn't load invites."))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!draft.email) { toast.error("Email is required."); return; }
        setSaving(true);
        try {
            const res = await adminApi.createInvite({
                email: draft.email.trim().toLowerCase(),
                role: draft.role,
                notes: draft.notes?.trim() || undefined,
                manager_id: draft.role === "master_admin" ? null : (draft.manager_id || undefined),
            });
            toast.success("Invite sent.");
            setLastLink(res.link);
            setDraft({ email: "", role: "agent", notes: "", manager_id: null });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send.");
        } finally { setSaving(false); }
    };

    const resend = async (id) => {
        try {
            const res = await adminApi.resendInvite(id);
            toast.success("Resent.");
            setLastLink(res.link);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't resend."); }
    };

    const revoke = async (id) => {
        if (!window.confirm("Revoke this invite?")) return;
        try { await adminApi.revokeInvite(id); toast.success("Revoked."); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't revoke."); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this invite?")) return;
        try { await adminApi.deleteInvite(id); toast.success("Deleted."); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete."); }
    };

    return (
        <>
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[12px] text-mm-muted">{rows.length} invite{rows.length === 1 ? "" : "s"}</div>
                <button onClick={() => { setModalOpen(true); setLastLink(null); }} className="mm-btn-primary !py-1.5 text-sm" data-testid="invite-new-btn">
                    <UserPlus size={13} /> Send Invite
                </button>
            </div>
            {lastLink && (
                <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="invite-last-link">
                    <ShieldCheck size={12} />
                    <span className="flex-1 truncate font-mono">{lastLink}</span>
                    <button
                        onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Link copied."); }}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                        data-testid="invite-copy-link"
                    >
                        <Copy size={10} /> Copy
                    </button>
                </div>
            )}
            <div className="mm-card overflow-hidden" data-testid="invites-table">
                {loading ? (
                    <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-10 text-center text-mm-muted">No invites yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <th className="px-3 py-2.5 text-left font-medium">Email</th>
                                <th className="px-3 py-2.5 text-left font-medium">Role</th>
                                <th className="px-3 py-2.5 text-left font-medium">Invited by</th>
                                <th className="px-3 py-2.5 text-left font-medium">Expires</th>
                                <th className="px-3 py-2.5 text-left font-medium">Status</th>
                                <th className="px-3 py-2.5 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((inv) => (
                                <tr key={inv.id} className="border-t border-mm-line" data-testid={`invite-row-${inv.id}`}>
                                    <td className="px-3 py-2.5 font-medium text-mm-navy">{inv.email}</td>
                                    <td className="px-3 py-2.5"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${ROLE_PILL[inv.role] || ""}`}>{inv.role.replace("_", " ")}</span></td>
                                    <td className="px-3 py-2.5 text-mm-ink">{inv.invited_by_name || "—"}</td>
                                    <td className="px-3 py-2.5 text-[11px] text-mm-muted">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[inv.status] || ""}`}>{inv.status}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center justify-end gap-1">
                                            {(inv.status === "pending" || inv.status === "expired") && (
                                                <button onClick={() => resend(inv.id)} className="mm-btn-outline !py-1 !px-2 text-[11px]" data-testid={`invite-resend-${inv.id}`}>
                                                    <RefreshCw size={11} /> Resend
                                                </button>
                                            )}
                                            {inv.status === "pending" && (
                                                <button onClick={() => revoke(inv.id)} className="mm-btn-outline !py-1 !px-2 text-[11px]" data-testid={`invite-revoke-${inv.id}`} style={{ color: "#9F1239", borderColor: "#FECDD3" }}>
                                                    <X size={11} /> Revoke
                                                </button>
                                            )}
                                            {inv.status !== "accepted" && (
                                                <button onClick={() => del(inv.id)} className="rounded p-1 text-rose-700 hover:bg-rose-50" data-testid={`invite-delete-${inv.id}`}>
                                                    <Trash2 size={11} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" role="dialog" data-testid="invite-modal">
                    <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                        <h3 className="mm-display text-xl text-mm-navy">Send invitation</h3>
                        <p className="mt-1 text-[11px] text-mm-muted">A branded email goes out with the invite link. They click → set a password → land logged in.</p>
                        <div className="mt-4 space-y-3">
                            <label className="block">
                                <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Email</span>
                                <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="name@company.com" data-testid="invite-modal-email" />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Role</span>
                                <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value, manager_id: e.target.value === "master_admin" ? null : draft.manager_id })} className="w-full rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="invite-modal-role">
                                    {me?.role === "master_admin" && <option value="master_admin">Master Admin</option>}
                                    <option value="admin">Admin</option>
                                    <option value="agent">Agent</option>
                                </select>
                            </label>
                            {draft.role !== "master_admin" && (
                                <label className="block">
                                    <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Assign upline (optional)</span>
                                    <select
                                        value={draft.manager_id || ""}
                                        onChange={(e) => setDraft({ ...draft, manager_id: e.target.value || null })}
                                        className="w-full rounded-md border border-mm-line px-2 py-1.5 text-sm"
                                        data-testid="invite-modal-manager"
                                    >
                                        <option value="">(No upline)</option>
                                        {users.filter((u) => u.role === "admin" || u.role === "master_admin").map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name} — {(u.role || "").replace("_", " ")}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label className="block">
                                <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Internal notes (optional)</span>
                                <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2} className="w-full rounded-md border border-mm-line p-2 text-sm" data-testid="invite-modal-notes" placeholder="Who is this and why?" />
                            </label>
                        </div>
                        <div className="mt-4 flex items-center justify-end gap-2">
                            <button onClick={() => setModalOpen(false)} className="mm-btn-outline" disabled={saving}>Cancel</button>
                            <button onClick={create} disabled={saving || !draft.email} className="mm-btn-primary disabled:opacity-50" data-testid="invite-modal-send">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
